// Editor de las plantillas de notificación de las alarmas (netvoice.alarm_notif_templates).
//
// Vive en Ajustes y no en la pantalla de Alertas por pedido de Carlos: es configuración, no
// operación. Ojo con la separación que hay detrás — los SECRETOS del envío (servidor SMTP,
// token de Telegram) están en la solapa "SMTP / Email" y en el .env; acá va solo el CONTENIDO
// y a quién le llega.
//
// Es autónomo: se carga sus propios datos. El único acople con la página que lo hospeda es
// `onMsg`, para que el banner de éxito/error salga arriba de todo como el resto de Ajustes.
import { useState, useEffect, useCallback } from "react";
import api from "../api";

function TemplateModal({ tpl, isNew, placeholders, onClose, onSaved }) {
  const [form, setForm] = useState({
    alert_key: tpl.alert_key || "",
    label: tpl.label || "",
    channel_email: (tpl.channel_email || "yes") === "yes",
    channel_telegram: (tpl.channel_telegram || "yes") === "yes",
    subject_tpl: tpl.subject_tpl || "",
    body_tpl: tpl.body_tpl || "",
    telegram_tpl: tpl.telegram_tpl || "",
    to_email: tpl.to_email || "",
    to_telegram: tpl.to_telegram || "",
    activo: (tpl.activo || "yes") === "yes",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [focused, setFocused] = useState("body_tpl");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // El chip inserta el placeholder al final del último campo que se tocó.
  const insert = (ph) => set(focused, (form[focused] || "") + "{" + ph + "}");

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.put(`/v1/alerts/templates/${encodeURIComponent(form.alert_key)}`, {
        label: form.label,
        channel_email: form.channel_email ? "yes" : "no",
        channel_telegram: form.channel_telegram ? "yes" : "no",
        subject_tpl: form.subject_tpl,
        body_tpl: form.body_tpl,
        telegram_tpl: form.telegram_tpl,
        to_email: form.to_email,
        to_telegram: form.to_telegram,
        activo: form.activo ? "yes" : "no",
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error guardando la plantilla");
    } finally { setBusy(false); }
  };

  // Previsualiza el BORRADOR en pantalla, no lo que está guardado.
  const doPreview = async () => {
    setErr(null);
    try {
      const r = await api.post(`/v1/alerts/templates/${encodeURIComponent(form.alert_key)}/preview`, {
        severity: "critical", event: "firing",
        summary: "Ejemplo: la condición se cumplió",
        umbral: "85", label: form.label,
        subject_tpl: form.subject_tpl, body_tpl: form.body_tpl,
        telegram_tpl: form.telegram_tpl,
      });
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo previsualizar");
    }
  };

  const ta = (k, rows) => ({
    value: form[k] || "", rows,
    onFocus: () => setFocused(k),
    onChange: e => set(k, e.target.value),
  });

  return (
    <div className="nv-modal-overlay" onClick={onClose}>
      <div className="nv-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="nv-modal-header">
          <h3>📨 {isNew ? "Nueva plantilla" : "Editar plantilla"} — <span style={{ fontFamily: "var(--font-mono)" }}>{form.alert_key}</span></h3>
          <button className="nv-icon-btn" onClick={onClose}>✕</button>
        </div>

        {err && <div className="nv-alert nv-alert-err" style={{ margin: "10px 0" }}>{err}</div>}

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "4px 0 12px" }}>
          <span style={{ fontSize: 12, color: "var(--text-dim)", alignSelf: "center" }}>Insertar en «{focused}»:</span>
          {(placeholders || []).map(ph => (
            <button key={ph} className="nv-btn nv-btn-sm" onClick={() => insert(ph)}
                    style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{"{" + ph + "}"}</button>
          ))}
        </div>

        <div className="nv-form-grid">
          <label>Nombre legible (también disponible como <code>{"{etiqueta}"}</code>)
            <input type="text" value={form.label} onChange={e => set("label", e.target.value)}
                   placeholder="ej: Disco alto" />
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.channel_email} onChange={e => set("channel_email", e.target.checked)} />
            Enviar por Email
          </label>
          <label>Asunto del email
            <input type="text" value={form.subject_tpl}
                   onFocus={() => setFocused("subject_tpl")}
                   onChange={e => set("subject_tpl", e.target.value)}
                   placeholder="[Eneural] {icono} {estado} — {etiqueta}" />
          </label>
          <label>Cuerpo del email
            <textarea {...ta("body_tpl", 6)} />
          </label>
          <label>Destinatarios de email (coma)
            <input type="text" value={form.to_email} onChange={e => set("to_email", e.target.value)}
                   placeholder="noc@eneural.org,soporte@eneural.org (vacío = override de la regla, luego ALERT_EMAIL_TO)" />
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.channel_telegram} onChange={e => set("channel_telegram", e.target.checked)} />
            Enviar por Telegram
          </label>
          <label>Mensaje de Telegram (vacío = usa el cuerpo del email)
            <textarea {...ta("telegram_tpl", 4)} />
          </label>
          <label>Chat IDs de Telegram (coma)
            <input type="text" value={form.to_telegram} onChange={e => set("to_telegram", e.target.value)}
                   placeholder="-1001234567890 (vacío = override de la regla, luego TELEGRAM_CHAT_IDS)" />
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.activo} onChange={e => set("activo", e.target.checked)} />
            Activa (si se desactiva, la alarma cae a la plantilla de familia o al comodín)
          </label>
        </div>

        {preview && (
          <div style={{ marginTop: 12, padding: 10, background: "var(--bg-raised)", borderRadius: "var(--r-sm)" }}>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 6 }}>
              Vista previa · severidad <strong>critical</strong>, estado <strong>ACTIVA</strong>
            </div>
            <div style={{ fontSize: 13 }}><strong>Asunto:</strong> {preview.subject}</div>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, margin: "6px 0" }}>{preview.body}</pre>
            <div style={{ fontSize: 12, color: "var(--text-dim)" }}>Telegram:</div>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, margin: "4px 0 0" }}>{preview.telegram}</pre>
          </div>
        )}

        <div className="nv-modal-actions">
          <button className="nv-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="nv-btn-ghost" onClick={doPreview} disabled={busy}>Previsualizar</button>
          <button className="nv-btn-primary" onClick={save} disabled={busy || !form.body_tpl.trim()}>
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlantillasAlarmas({ onMsg }) {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ placeholders: [], known_keys: [], wildcard: "__default__" });
  const [editing, setEditing] = useState(null);   // {tpl, isNew}
  const [newKey, setNewKey] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/alerts/templates");
      setTemplates(r.data?.data || []);
      setMeta(r.data || {});
    } catch (e) {
      onMsg?.({ type: "error", text: "No se pudieron cargar las plantillas: " + (e?.response?.data?.detail || e.message) });
    } finally { setLoading(false); }
  }, [onMsg]);

  useEffect(() => { load(); }, [load]);

  // Alarmas que el watcher puede disparar y todavía no tienen plantilla propia.
  const pending = (meta.known_keys || []).filter(k => !templates.some(t => t.alert_key === k.alert_key));

  const remove = async (key) => {
    try {
      await api.delete(`/v1/alerts/templates/${encodeURIComponent(key)}`);
      onMsg?.({ type: "success", text: `Plantilla ${key} eliminada; el evento vuelve a la de familia o al comodín.` });
      load();
    } catch (e) {
      onMsg?.({ type: "error", text: e?.response?.data?.detail || "No se pudo eliminar la plantilla" });
    }
  };

  return (
    <div className="nv-card" style={{ marginBottom: 16 }}>
      <div className="nv-card-header"><span className="nv-card-title">📨 Mensajes de las alarmas</span>
        <span style={{ fontSize: 12, color: "var(--text-dim)" }}>contenido y destinatarios por canal</span></div>
      <p style={{ fontSize: 12, color: "var(--text-dim)", margin: "0 0 12px" }}>
        Cada alarma busca su plantilla en este orden: <strong>su propia key</strong> → <strong>su familia</strong>{" "}
        (<code>custom</code>, <code>kpi</code>, <code>sip</code>, <code>watch</code>) → <strong>{meta.wildcard}</strong> (comodín).
        Los destinatarios de acá pisan al «override» de la regla y, a diferencia de aquél, se configuran por
        separado para email y para Telegram. Las credenciales de envío no van acá: están en la solapa
        <strong> ✉ SMTP / Email</strong> y en el <code>.env</code>.
      </p>

      {pending.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12, flexWrap: "wrap" }}>
          <div className="nv-form-field" style={{ flex: "1 1 260px" }}>
            <label className="nv-label">Alarma sin plantilla propia</label>
            <select className="nv-select" value={newKey} onChange={e => setNewKey(e.target.value)}>
              <option value="">— elegir —</option>
              {pending.map(k => <option key={k.alert_key} value={k.alert_key}>{k.alert_key} — {k.label}</option>)}
            </select>
          </div>
          <button className="nv-btn nv-btn-primary" disabled={!newKey}
                  onClick={() => {
                    const k = pending.find(x => x.alert_key === newKey);
                    setEditing({ isNew: true, tpl: { alert_key: newKey, label: k?.label || "" } });
                  }}>+ Crear plantilla</button>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Cargando…</div>
      ) : templates.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Sin plantillas: las alarmas se envían con el texto de fábrica del watcher.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {templates.map(t => (
            <div key={t.alert_key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "var(--bg-raised)", borderRadius: "var(--r-sm)" }}>
              <span style={{ flex: 1, fontSize: 13 }}>
                <strong style={{ fontFamily: "var(--font-mono)" }}>{t.alert_key}</strong>
                {t.label ? <span style={{ color: "var(--text-dim)" }}> · {t.label}</span> : null}
              </span>
              <span style={{ fontSize: 11, color: "var(--text-dim)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={`email: ${t.to_email || "(por defecto)"} · telegram: ${t.to_telegram || "(por defecto)"}`}>
                ✉ {t.to_email || "por defecto"} · ✈ {t.to_telegram || "por defecto"}
              </span>
              {t.channel_email !== "yes" && <span className="nv-badge">sin email</span>}
              {t.channel_telegram !== "yes" && <span className="nv-badge">sin telegram</span>}
              {t.activo !== "yes" && <span className="nv-badge">off</span>}
              <button className="nv-btn nv-btn-sm" onClick={() => setEditing({ isNew: false, tpl: t })}>Editar</button>
              <button className="nv-btn nv-btn-danger nv-btn-sm" disabled={t.alert_key === meta.wildcard}
                      title={t.alert_key === meta.wildcard ? "El comodín no se borra" : "Eliminar"}
                      onClick={() => remove(t.alert_key)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateModal
          tpl={editing.tpl}
          isNew={editing.isNew}
          placeholders={meta.placeholders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setNewKey(""); onMsg?.({ type: "success", text: "Plantilla guardada. Se aplica en la próxima alarma, sin reiniciar el watcher." }); load(); }}
        />
      )}
    </div>
  );
}
